import { Component, OnDestroy, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { ReplaySubject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { AppState } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import { PHOTO_BOOTH } from 'entity/SocialSettings';
import User from 'entity/User';

import Message from '../message/message';
import MessageType from '../message/message-type';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../modal-simple-social/modal-simple-social.component';
import { PhotoBoothViewerComponent, PhotoViewerData } from '../photo-booth-viewer/photo-booth-viewer.component';
import { PhotoBoothService, PhotoBoothPicture } from '../services/photo-booth.service';
import { SceneService } from '../services/scene.service';
import { SocialMessageService } from '../services/social-message.service';
import { socialAppSelectors } from '../state/app';

@Component({
  selector: 'app-photo-booth',
  templateUrl: './photo-booth.component.html',
  styleUrls: ['./photo-booth.component.scss'],
  standalone: false,
})
export class PhotoBoothComponent extends Loggable implements OnDestroy {
  @Input() public social?: Social;
  public isLoading = false;
  public loadingText = '';
  public isPhotoBoothEnabled = false;
  public allowPhotoBooth = false;

  private user?: User;
  private isHost = false;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    private dialog: MatDialog,
    private messageService: SocialMessageService,
    public photoBoothService: PhotoBoothService,
    private sceneService: SceneService,

    private store: Store<AppState>,
  ) {
    super();
    this.setDisplayName('PhotoBoothComponent');
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => {
        this.user = user;
      });

    combineLatest([this.store.select(socialAppSelectors.isHost), this.sceneService.isFeatureAllowed(PHOTO_BOOTH)])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isHost, canPhotoBooth]) => {
        this.isHost = isHost ?? false;
        this.allowPhotoBooth = canPhotoBooth;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_photo_booth)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((photoBoothEnabled) => (this.isPhotoBoothEnabled = photoBoothEnabled));
  }

  public ngOnDestroy(): void {
    this.photoBoothService.destroy();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public showPhotoBoothModal(): void {
    const dialog = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data: {
        modalTitle: 'Photo booth is ready to start',
        text: 'The photo booth will take 3 consecutive photos of your room, which you can save and share with your friends and colleagues!',
        primaryButtonText: 'Snap Photos',
        primaryButtonIcon: 'camera_alt',
      },
    });
    dialog
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result === 'primary') {
          this.photoBoothService.reset();
          this.photoBoothApproved();
        }
      });
  }

  private photoBoothApproved(): void {
    this.photoBoothService.boothStartedByUser = true;
    this.photoBoothService.boothInSession = false;
    this.startPhotoTakingProcess([
      { message: '{{name}} turned on the photo booth! Smile for the first picture in', duration: 5 },
      { message: 'Great! Snapping the next picture in', duration: 3 },
      { message: 'Last one! Smile in', duration: 3 },
    ]).catch((error) => {
      this.error('Failed to start photo booth', error);
    });
  }

  private async startPhotoTakingProcess(notifications: PhotoBoothNotification[]): Promise<void> {
    for (const notification of notifications) {
      this.sendMessageToPlayersInView(notification);
      const canceled = await this.photoBoothService.startCountdown(
        notification.message.replace('{{name}}', 'You'),
        notification.duration,
      );
      if (canceled) {
        return;
      }
      await this.photoBoothService.takePhoto(this.social?.theme?.colorGradientBackground);
    }
    this.isLoading = true;
    this.loadingText =
      'We’re loading the preview of your pictures. This may take a minute. Don’t refresh or click the back button.';
    const gif = await this.photoBoothService.createGif(this.photoBoothService.photos);
    this.photoBoothService.photos.push(gif);
    const viewerData = await this.generateViewerData();
    this.isLoading = false;
    this.openViewer(viewerData);
  }

  private sendMessageToPlayersInView(notification: PhotoBoothNotification): void {
    assertDefined(this.user);
    const message: Message = {
      type: MessageType.PHOTO_BOOTH_START,
      message: notification.message.replace('{{name}}', `${formatUserName(this.user)}`),
      photoBoothData: { notificationDuration: notification.duration },
      onlyPlayers: this.sceneService.getPlayersIds(),
    };
    this.messageService.sendMessage(message);
  }

  private async generateViewerData(): Promise<PhotoViewerData> {
    const photoBoothPictures: PhotoBoothPicture[] = [];
    this.photoBoothService.photos.forEach((url, id) => photoBoothPictures.push({ url, id }));
    const framedPictures = await this.photoBoothService.addPreciateFrame(
      photoBoothPictures,
      moment().format('MM.DD.YYYY'),
      this.social?.name ?? '',
    );
    return { photoBoothPictures, framedPictures };
  }

  private openViewer(data: PhotoViewerData): void {
    this.photoBoothService.boothStartedByUser = false;
    const dialog = this.dialog.open(PhotoBoothViewerComponent, {
      panelClass: 'photo-booth-viewer',
      data,
    });
    dialog.componentInstance.socialName = (this.social?.name ?? '').replace(/[^\w\s]/gi, '');
    dialog
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        // TODO: Revisit the logic about social media integration
        // if (!isNaN(idSelectedToShare)) {
        //   const selectedFramedPicture = data.framedPictures.find((fp) => fp.id === idSelectedToShare);
        //   if (!selectedFramedPicture) {
        //     return;
        //   }
        //   const url = await this.uploadPicture(selectedFramedPicture);
        //   this.dialog.open(SocialMediaShareModalComponent, {
        //     panelClass: 'photo-booth-viewer',
        //     data: {
        //       url,
        //       description: `This pic was snapped on #scootsocial - Create your own room at https://scoot.app`,
        //     },
        //   });
        // }
      });
  }

  // TODO: Revisit the logic about social media integration
  // public async uploadPicture(imageToShare: FramedPicture): Promise<string> {
  //   this.isLoading = true;
  //   this.loadingText = 'Preparing your picture...';
  //   const { base64, height, width } = imageToShare;
  //   const type = imageToShare.type.split('/')[1];
  //   try {
  //     const uploadedImage = await this.socialMediaShareService.uploadImage(type, width, height, base64);
  //     const imageURI = `${uploadedImage.imageId}.${type}`;
  //     const baseUrl = 'https://scoot.app';
  //     return `${baseUrl}/imgs/public/${imageURI}`;
  //   } catch (error) {
  //     this.error('Failed to upload picture', error);
  //     throw error;
  //   } finally {
  //     this.isLoading = false;
  //   }
  // }
}

interface PhotoBoothNotification {
  message: string;
  duration: number;
}
